import { omit } from "lodash-es";

import type { DeepPartial, Dictionary, DictionaryMethod, SearchDirection, SearchFilter, SearchLanguage } from "@/types";

import { DictionariesHelper, DictionaryMethodHelper } from ".";

export const DEFAULT_DIRECTION: SearchDirection = "two-way";

export function filterMethods(
  prioritizedDictionaries: Dictionary[],
  filter: SearchFilter,
  defaultMethods: DictionaryMethod[] = []
): DictionaryMethod[] {
  if (!filter.sourceLanguage) {
    // No filter is set, just return the default methods
    return defaultMethods;
  }

  return prioritizedDictionaries.reduce<DictionaryMethod[]>((methods, dictionary) => {
    // Languageless company dictionaries are always included, regardless of user selected language filter.
    if (
      DictionariesHelper.isCompanyDictionary(dictionary) &&
      !DictionariesHelper.isCompanyDictionaryWithMethods(dictionary)
    ) {
      methods.push(...dictionary.methods);
      return methods;
    }

    dictionary.methods.forEach((method) => {
      const methodSourceLanguage = DictionaryMethodHelper.getSourceLanguage(method);
      const methodTargetLanguage = DictionaryMethodHelper.getTargetLanguage(method);

      if (filter.direction === "two-way") {
        if (filter.sourceLanguage && filter.targetLanguage) {
          if (
            methodSourceLanguage?.code === filter.sourceLanguage.code &&
            methodTargetLanguage?.code === filter.targetLanguage.code
          ) {
            methods.push(method);
          }

          if (
            methodSourceLanguage?.code === filter.targetLanguage.code &&
            methodTargetLanguage?.code === filter.sourceLanguage.code
          ) {
            methods.push(method);
          }
        } else {
          const methodLanguageCodes = [methodSourceLanguage?.code, methodTargetLanguage?.code];
          if (filter.sourceLanguage && methodLanguageCodes.includes(filter.sourceLanguage.code)) {
            methods.push(method);
            return;
          }

          // It is not possible to set a target language without a source language.
          // If it was, that case should be handled here.
        }
      }

      if (filter.direction === "one-way") {
        if (
          methodSourceLanguage?.code === filter.sourceLanguage?.code &&
          (!filter.targetLanguage || methodTargetLanguage?.code === filter.targetLanguage.code)
        ) {
          methods.push(method);
          return methods;
        }
      }
    });

    return methods;
  }, []);
}

/**
 * Swap the languages of the given filter.
 * If the filter is two-way, it will not be swapped, as there is no logical reason to do so.
 * If only a source language is set, the target language will be set by finding the opposite method in the given dictionary.
 */
export function swap(filter: SearchFilter, dictionary?: Dictionary): SearchFilter | undefined {
  // There is no reason to swap a two-way filter
  if (filter.direction !== "one-way") {
    return;
  }

  const { sourceLanguage, targetLanguage } = filter;

  // No source language, so we can't do anything
  if (!sourceLanguage) {
    return;
  }

  // Filter is specific one-way, like da -> de, so we can easily swap the languages
  if (targetLanguage && sourceLanguage !== targetLanguage) {
    filter.sourceLanguage = targetLanguage;
    filter.targetLanguage = sourceLanguage;
    return filter;
  }

  if (!dictionary) {
    return;
  }

  // If the dictionary is monolingual, there is no reason to swap languages
  if (DictionaryMethodHelper.getLanguages(dictionary.methods).length === 1) {
    return;
  }

  // Filter is not specific, like da -> "all", so we find the dictionary's opposite method
  const method = dictionary.methods.find((m) => m.targetLanguageCode === sourceLanguage.code);
  if (!method) {
    return;
  }

  filter.sourceLanguage = DictionaryMethodHelper.getSourceLanguage(method);
  return filter;
}

export function convertToSearchFilter(dictionary: Dictionary): SearchFilter {
  const sourceLanguages: SearchLanguage[] = [];
  const targetLanguages: SearchLanguage[] = [];

  dictionary.methods.forEach((method) => {
    sourceLanguages.push({ code: method.sourceLanguageCode, name: method.sourceLanguage });
    targetLanguages.push({ code: method.targetLanguageCode, name: method.targetLanguage });
  });

  const searchFilter: SearchFilter = {
    direction: "two-way"
  };

  if (sourceLanguages.length === 0) {
    return searchFilter;
  }

  searchFilter.sourceLanguage = sourceLanguages[0];
  if (targetLanguages.length === 0) {
    return searchFilter;
  }

  searchFilter.targetLanguage = targetLanguages[0];
  return searchFilter;
}

export function convertToDirection(filter: SearchFilter, methods: DictionaryMethod[]): string | undefined {
  if (!filter.sourceLanguage) {
    return;
  }

  if (filter.direction === "two-way") {
    return;
  }

  let predicate = (method: DictionaryMethod) => method.sourceLanguageCode === filter.sourceLanguage?.code;
  if (filter.targetLanguage) {
    predicate = (method) =>
      method.sourceLanguageCode === filter.sourceLanguage?.code &&
      method.targetLanguageCode === filter.targetLanguage?.code;
  }

  return methods.find(predicate)?.id;
}

export function prepareForSerialization(filter: SearchFilter): DeepPartial<SearchFilter> | undefined {
  if (!filter.sourceLanguage) {
    return;
  }

  // Language names are irrelevant when encoding
  const cleanedFilter = { ...filter } as DeepPartial<SearchFilter>;
  cleanedFilter.sourceLanguage = omit(filter.sourceLanguage, "name");

  if (filter.targetLanguage) {
    cleanedFilter.targetLanguage = omit(filter.targetLanguage, "name");
  }

  return cleanedFilter;
}

export function restore(partialFilter: DeepPartial<SearchFilter>, availableLanguages: SearchLanguage[]): SearchFilter {
  const sourceLanguage = availableLanguages.find((language) => language.code === partialFilter.sourceLanguage?.code);
  const targetLanguage = availableLanguages.find((language) => language.code === partialFilter.targetLanguage?.code);

  return { sourceLanguage, direction: partialFilter.direction ?? DEFAULT_DIRECTION, targetLanguage };
}

export function dictionaryIsCompatibleWithSearchFilter(dictionary: Dictionary, filter: SearchFilter) {
  const methodsFilteredBySearchFilter = filterMethods(
    [dictionary],
    filter,
    DictionariesHelper.getMethods([dictionary])
  );

  return methodsFilteredBySearchFilter.length > 0;
}
